'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { InfoIcon, Wallet, Building2, CheckCircle2, XCircle, Loader2, AlertTriangle } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { useValidateCryptoAddress } from '@/lib/api/crypto'
import { useTranslation } from 'react-i18next'

const PROVIDERS: { value: string; label: string; min: number; note?: string }[] = [
  { value: 'moonpay', label: 'MoonPay (Recommended)', min: 20 },
  { value: 'stripe', label: 'Stripe', min: 2, note: 'USD only, USA only' },
  { value: 'rampnetwork', label: 'Ramp Network', min: 4, note: 'USD only' },
  { value: 'robinhood', label: 'Robinhood', min: 5, note: 'USD only' },
  { value: 'topper', label: 'Topper', min: 10 },
  { value: 'bitnovo', label: 'Bitnovo', min: 10 },
  { value: 'simpleswap', label: 'SimpleSwap', min: 10 },
  { value: 'mercuryo', label: 'Mercuryo', min: 15 },
  { value: 'revolut', label: 'Revolut', min: 15 },
  { value: 'transak', label: 'Transak', min: 15 },
  { value: 'banxa', label: 'Banxa', min: 20 },
  { value: 'sardine', label: 'Sardine', min: 30 },
  { value: 'particle', label: 'Particle', min: 30 },
  { value: 'simplex', label: 'Simplex', min: 50 },
  { value: 'transfi', label: 'TransFi', min: 70, note: 'USD only' },
  { value: 'interac', label: 'Interac', min: 100, note: 'CAD only' },
  { value: 'upi', label: 'UPI', min: 100, note: 'INR only' },
]

const POLYGON_ADDRESS_REGEX = /^0x[0-9a-fA-F]{40}$/

const cryptoFormSchema = z.object({
  label: z.string().min(1, 'Button label is required'),
  provider: z.string(),
  providers: z.array(z.object({ id: z.string(), enabled: z.boolean() })).optional(),
  walletAddress: z.string()
    .optional()
    .refine(
      (val) => !val || POLYGON_ADDRESS_REGEX.test(val),
      'Invalid address format. Must be a 0x-prefixed 42-character hex string.',
    ),
})

type FormValues = z.infer<typeof cryptoFormSchema>

interface CryptoFormProps {
  defaultValues?: Record<string, any>
  onSubmit: (data: Record<string, any>) => Promise<void>
  isLoading?: boolean
}

export function CryptoForm({
  defaultValues = {},
  onSubmit,
  isLoading = false,
}: CryptoFormProps) {
  const { t } = useTranslation('integrations')
  const settlementMode: 'dedicated' | 'pooled' = defaultValues.settings?.settlementMode ?? 'pooled'
  const validateAddressMutation = useValidateCryptoAddress()
  const validateRef = useRef(validateAddressMutation.mutateAsync)
  validateRef.current = validateAddressMutation.mutateAsync
  const [addressValidation, setAddressValidation] = useState<{
    status: 'idle' | 'validating' | 'valid' | 'invalid'
    message: string | null
    warning: string | null
    checksumAddress: string | null
  }>({ status: 'idle', message: null, warning: null, checksumAddress: null })
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Migrate legacy single provider to providers array
  const existingProviders: { id: string; enabled: boolean }[] = defaultValues.settings?.providers
    ?? (defaultValues.settings?.provider
      ? [{ id: defaultValues.settings.provider, enabled: true }]
      : [])

  const form = useForm<FormValues>({
    resolver: zodResolver(cryptoFormSchema),
    defaultValues: {
      label: defaultValues.settings?.label ?? 'Pay with Card',
      provider: defaultValues.settings?.provider ?? 'moonpay',
      providers: existingProviders,
      walletAddress: defaultValues.credentials?.walletAddress ?? '',
    },
  })

  const { register, handleSubmit, watch, setValue, formState: { errors } } = form
  const walletAddress = watch('walletAddress')
  const selectedProviders = watch('providers') || []
  const selectedProvider = PROVIDERS.find((p) => p.value === watch('provider'))
  const [providerSearch, setProviderSearch] = useState('')

  const toggleProvider = (providerId: string) => {
    const current = selectedProviders || []
    const existing = current.find((p) => p.id === providerId)
    let updated: { id: string; enabled: boolean }[]
    if (existing) {
      updated = current.map((p) =>
        p.id === providerId ? { ...p, enabled: !p.enabled } : p,
      )
    } else {
      updated = [...current, { id: providerId, enabled: true }]
    }
    setValue('providers', updated)
    if (updated.some((p) => p.enabled)) setProviderError(null)
  }

  const isProviderEnabled = (providerId: string) => {
    return selectedProviders?.some((p) => p.id === providerId && p.enabled) ?? false
  }

  const enabledProviderCount = selectedProviders?.filter((p) => p.enabled).length ?? 0
  const [providerError, setProviderError] = useState<string | null>(null)

  const validateAddress = useCallback((address: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!address || !POLYGON_ADDRESS_REGEX.test(address)) {
      setAddressValidation({ status: 'idle', message: null, warning: null, checksumAddress: null })
      return
    }

    setAddressValidation({ status: 'validating', message: null, warning: null, checksumAddress: null })
    debounceRef.current = setTimeout(async () => {
      try {
        const result = await validateRef.current({ address })
        if (result.valid) {
          setAddressValidation({
            status: 'valid',
            message: result.checksumAddress !== address ? `Checksum: ${result.checksumAddress}` : null,
            warning: result.warning ?? null,
            checksumAddress: result.checksumAddress,
          })
          if (result.checksumAddress && result.checksumAddress !== address) {
            setValue('walletAddress', result.checksumAddress)
          }
        } else {
          setAddressValidation({
            status: 'invalid',
            message: result.error,
            warning: null,
            checksumAddress: null,
          })
        }
      } catch {
        setAddressValidation({ status: 'idle', message: null, warning: null, checksumAddress: null })
      }
    }, 600)
  }, [setValue])

  useEffect(() => {
    if (walletAddress) validateAddress(walletAddress)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [walletAddress, validateAddress])

  const handleFormSubmit = async (data: FormValues) => {
    if (data.walletAddress && addressValidation.status === 'invalid') return

    const enabledProvidersList = (data.providers || []).filter((p) => p.enabled)
    if (enabledProvidersList.length === 0) {
      setProviderError('Select at least one on-ramp provider.')
      return
    }
    setProviderError(null)
    // Keep legacy provider field for backward compat (first enabled or fallback)
    const primaryProvider = enabledProvidersList[0]?.id || data.provider

    await onSubmit({
      credentials: {
        walletAddress: addressValidation.checksumAddress || data.walletAddress || '',
      },
      settings: {
        label: data.label,
        provider: primaryProvider,
        providers: data.providers || [],
      },
    })
  }

  return (
    <form id="integration-config-form" onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      <Alert className="border-blue-200 bg-blue-50">
        <InfoIcon className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-900">{t('crypto.howItWorks')}</AlertTitle>
        <AlertDescription className="text-blue-800">
          <ul className="mt-1 space-y-1">
            <li>• {t('crypto.howStep1')}</li>
            <li>• {t('crypto.howStep2')}</li>
            <li>• {t('crypto.howStep3')}</li>
            <li>• {t('crypto.howStep4')}</li>
            <li>• {t('crypto.howStep5')}</li>
            <li>• {t('crypto.howStep6')}</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Settlement mode — read-only, managed by account manager */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base">{t('crypto.settlementMode')}</CardTitle>
              <CardDescription>{t('crypto.managedByAccountManager')}</CardDescription>
            </div>
            {settlementMode === 'dedicated' ? (
              <Badge variant="default" className="flex items-center gap-1.5">
                <Wallet className="h-3.5 w-3.5" />
                {t('crypto.directToWallet')}
              </Badge>
            ) : (
              <Badge variant="secondary" className="flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {t('crypto.pooledTagadaPay')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {settlementMode === 'dedicated'
              ? t('crypto.dedicatedDescription')
              : t('crypto.pooledDescription')}
            {' '}{t('crypto.contactAccountManager')}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('crypto.paymentSettings')}</CardTitle>
          <CardDescription>{t('crypto.paymentSettingsDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="label">
              {t('crypto.buttonLabel')}
              <span className="ml-1 text-destructive">*</span>
            </Label>
            <Input
              id="label"
              placeholder="Pay with Card"
              disabled={isLoading}
              {...register('label')}
            />
            <p className="text-xs text-muted-foreground">
              {t('crypto.buttonLabelHint')}
            </p>
            {errors.label && (
              <p className="text-xs text-destructive">{errors.label.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="walletAddress">
              {t('crypto.walletAddress')}
              {settlementMode === 'dedicated' && <span className="ml-1 text-destructive">*</span>}
            </Label>
            <div className="relative">
              <Input
                id="walletAddress"
                placeholder="0x..."
                disabled={isLoading}
                className="font-mono text-sm pr-10"
                {...register('walletAddress')}
              />
              {walletAddress && POLYGON_ADDRESS_REGEX.test(walletAddress) && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {addressValidation.status === 'validating' && (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                  {addressValidation.status === 'valid' && !addressValidation.warning && (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                  {addressValidation.status === 'valid' && addressValidation.warning && (
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                  )}
                  {addressValidation.status === 'invalid' && (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {settlementMode === 'dedicated'
                ? t('crypto.dedicatedWalletHint')
                : t('crypto.pooledWalletHint')}
            </p>
            {errors.walletAddress && (
              <p className="text-xs text-destructive">{errors.walletAddress.message}</p>
            )}
            {addressValidation.status === 'invalid' && addressValidation.message && (
              <p className="text-xs text-destructive">{addressValidation.message}</p>
            )}
            {addressValidation.status === 'valid' && addressValidation.warning && (
              <p className="text-xs text-amber-600">{addressValidation.warning}</p>
            )}
            {addressValidation.status === 'valid' && addressValidation.message && (
              <p className="text-xs text-green-600">{addressValidation.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('crypto.onRampProviders')}</Label>
            <p className="text-muted-foreground text-xs mb-2">
              {t('crypto.onRampHint')}
              {enabledProviderCount > 0 && (
                <span className="ml-1 font-medium text-foreground">{t('crypto.selected', { count: enabledProviderCount })}</span>
              )}
            </p>
            <div className="rounded-md border">
              <div className="sticky top-0 border-b bg-background p-1.5">
                <Input
                  placeholder={t('crypto.searchProviders')}
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            <div className="max-h-56 overflow-y-auto p-1 space-y-0.5">
              {PROVIDERS.filter((p) =>
                !providerSearch || p.label.toLowerCase().includes(providerSearch.toLowerCase()) ||
                p.value.toLowerCase().includes(providerSearch.toLowerCase()) ||
                (p.note && p.note.toLowerCase().includes(providerSearch.toLowerCase()))
              ).map((p) => {
                const enabled = isProviderEnabled(p.value)
                return (
                  <div
                    key={p.value}
                    className={cn(
                      'flex items-center gap-3 rounded-md px-3 py-2 cursor-pointer transition-colors',
                      enabled ? 'bg-accent' : 'hover:bg-muted/50',
                    )}
                    onClick={() => toggleProvider(p.value)}
                  >
                    <div
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                        enabled ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground',
                      )}
                    >
                      {enabled && <CheckCircle2 className="h-3 w-3" />}
                    </div>
                    <span className={cn('text-sm flex-1', !enabled && 'text-muted-foreground')}>
                      {p.label}
                    </span>
                    <span className="text-muted-foreground text-xs">min ${p.min}</span>
                    {p.note && (
                      <Badge variant="outline" className="text-[10px] shrink-0">{p.note}</Badge>
                    )}
                  </div>
                )
              })}
            </div>
            </div>
            {providerError && (
              <p className="text-xs text-destructive mt-1">{providerError}</p>
            )}
          </div>
        </CardContent>
      </Card>

      <Alert className="border-amber-200 bg-amber-50">
        <AlertDescription className="text-sm text-amber-700">
          <span dangerouslySetInnerHTML={{ __html: t('crypto.pricingNote') }} />
        </AlertDescription>
      </Alert>
    </form>
  )
}
